import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  FilterAndSortComponent,
  FilterConfig,
  HeaderComponent,
  Scan,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SCANNER,
  SOFTLINE_FEATURE_TITLE,
  SortConfig,
  StepHeaderComponent,
  TitleStore,
  UploadFileCommand } from '@softline/application';
import {
  DateService,
  explore,
  FilterService,
  SortService,
  Store,
  withFilter,
  withSearch,
  withSort } from '@softline/core';
import {
  FormatPipe,
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { map, skip, Subscription } from 'rxjs';
import { Deactivateable } from '../../../../../../mde/materiallieferschein-scan/src/lib/guards/on-deactivate.guard';
import {
  SOFTLINE_CONFIG_LLR_KOMMISSIONIERUNG_STEPS,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE } from '../../kommissionierung.shared';
import { RemoteConfigService } from '../../services/remote-config.service';
import { LlrKommissionierungStore } from '../../store/kommissionierung.store';
import { LlrPalleteStore } from '../../store/palette.store';
import { Kommissionierung } from '../../types/kommissionierung';

import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-artikel-auswahl',
  imports: [
    CommonModule,
    FilterAndSortComponent,
    StepHeaderComponent,
    HeaderComponent,
    RouterModule,
    RepeatDirective,
    TranslatePipe],
  templateUrl: './artikel-auswahl.component.html',
  styleUrls: ['./artikel-auswahl.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [FormatPipe] })
export class ArtikelAuswahlComponent
  implements OnInit, OnDestroy, BackNavigable, Deactivateable
{
  kommissionierungAbschliessenCalled = false;

  private kommissionierung?: Kommissionierung;
  private kommissionierungSubscription?: Subscription;
  private scanSubscription?: Subscription;
  private idkopfbskomm?: number;
  private archiveKey?: string;

  readonly abgeschlosseneArtikelAnzeigen =
    this.remoteConfig.config.abgeschlosseneArtikelAnzeigen;
  readonly paletteEinzelEingabe = this.remoteConfig.config.paletteEinzelEingabe;

  readonly paletten = toSignal(
    this.store.observe(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
      LlrPalleteStore.getters.all
    )
  );

  readonly steps = SOFTLINE_CONFIG_LLR_KOMMISSIONIERUNG_STEPS;

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly kommissionierungBewegungen$ = this.kommissionierung$.pipe(
    map((komm) => komm?.bewebskomms)
  );

  formatPipe = inject(FormatPipe);

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.loading
  );

  readonly kommissionierungBewegungen = toSignal(this.kommissionierungBewegungen$);

  readonly explorer = explore(
    this.kommissionierungBewegungen,
    withSort(),
    withFilter(),
    withSearch()
  );

  filterConfig: FilterConfig = {
    properties: [
      {
        property: 'id',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BEWEGUNG_ID' },
      {
        property: 'bewebs.artikel.artikelnummer',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_ARTIKELNUMMER' },
      {
        property: 'bewebs.artikel.artbez1',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_ARTIKELBEZEICHNUNG' }] };

  sortConfig: SortConfig = {
    properties: [
      {
        property: 'id',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BEWEGUNG_ID' },
      {
        property: 'bewebs.artikel.artikelnummer',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_ARTIKELNUMMER' },
      {
        property: 'bewebs.artikel.artbez1',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_ARTIKELBEZEICHNUNG' }] };

  constructor(
    private store: Store,
    private router: Router,
    private sortService: SortService,
    private filterService: FilterService,
    private backNavigationService: BackNavigationService,
    private cdRef: ChangeDetectorRef,
    private remoteConfig: RemoteConfigService,
    private dateService: DateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.kommissionierungAbschliessenCalled = false;
    this.backNavigationService.set(this);

    this.kommissionierungSubscription = this.kommissionierung$.subscribe((komm) => {
      if (komm) {
        this.kommissionierung = komm;
        this.idkopfbskomm = komm?.id;
        this.archiveKey = komm?.archiveKey;
      }
    });

    const uploadFileCommand = new UploadFileCommand(this.store, {
      sources: 'photo',
      selectionMode: 'multi',
      archiveKey: this.archiveKey });

    uploadFileCommand.icon = 'fa-regular fa-camera';
    uploadFileCommand.name = '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_FOTOS_HOCHLADEN';

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: ArtikelAuswahlComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_WARENZUGANG_BUCHEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-check',
          execute: async () =>
            await this.router.navigate(['/llr-bskomm/warenzugang-abschliessen']) },
        uploadFileCommand,
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_ARTIKEL_SCANNEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-barcode-scan',
          execute: async () => this.scanArtikelPosition() }] });

    try {
      if (!this.kommissionierung) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
          LlrKommissionierungStore.actions.load
        );
      }

      if (this.kommissionierung) {
        const komm = await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
          LlrKommissionierungStore.actions.loadAktuelleKommissionierung,
          {
            idkopfbskomm: this.kommissionierung.id + '' }
        );

        this.store.commit(
          SOFTLINE_FEATURE_TITLE,
          TitleStore.mutations.setSubTitle,
          `${this.formatPipe.transform(komm.belegdatum, 'DATE.DEFAULT')}, ${
            komm.lieferant?.kurzbez
          } - ${komm.lieferant?.nummer}`
        );

        const offeneBewes = komm.bewebskomms.filter(
          (bewe) => bewe?.kommissionierDaten?.abgeschlossen === false
        );

        if (offeneBewes.length <= 0) {
          const result = await this.store.dispatch(
            SOFTLINE_FEATURE_MODAL,
            ModalStore.actions.ask,
            {
              title:
                '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.TITEL_ALLE_ARTIKEL_ABGESCHLOSSEN',
              question:
                '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.QUESTION_ALLE_ARTIKEL_ABGESCHLOSSEN_WEITERLEITEN' }
          );
          if (result === 'YES')
            await this.router.navigate(['/llr-bskomm/warenzugang-abschliessen']);
        }
      }
    } catch (e) {
      showRequestErrors(this.store, e);
    }
    this.scanSubscription = this.store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(skip(1))
      .subscribe(async (scan) => {
        await this.scanArtikelPosition(scan);
      });
  }

  async ngOnDestroy(): Promise<void> {
    if (this.kommissionierungSubscription && !this.kommissionierungSubscription.closed)
      this.kommissionierungSubscription.unsubscribe();

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      ArtikelAuswahlComponent
    );

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');

    this.store.commit(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
      LlrKommissionierungStore.mutations.setFilter,
      null
    );

    this.store.commit(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
      LlrKommissionierungStore.mutations.setSort,
      null
    );

    if (this.scanSubscription && !this.scanSubscription.closed)
      this.scanSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
    this.scanSubscription = undefined;
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/llr-bskomm/bestellungen']);
  }

  async canDeactivate(): Promise<boolean> {
    try {
      const kommissionierung = this.store.get(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.getters.data
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.kommissionierungAbbrechen,
        {
          idkopfbskomm: kommissionierung?.id + '' }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.TITEL_WARENZUGANG_ABGEBROCHEN',
          message: '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.LABEL_WARENZUGANG_ABGEBROCHEN' }
      );
      return true;
    } catch (e) {
      showRequestErrors(this.store, e);
      return false;
    }
  }

  private async scanArtikelPosition(scan?: Scan) {
    try {
      let scanParam;

      if (!scan) {
        scanParam = await this.store.dispatch(
          SOFTLINE_FEATURE_SCANNER,
          ScannerStore.actions.scan,
          {
            labelType: ['ean13', 'ean128', 'code39', 'code128', 'ean8'] }
        );
      } else {
        scanParam = scan;
      }

      if (!scanParam) return;

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.scanArtikelPosition,
        {
          idkopfbskomm: this.idkopfbskomm ? this.idkopfbskomm + '' : '',
          type: scanParam.labelType ?? '',
          code: scanParam.data }
      );

      if (!result) return;

      await this.artikelpositionUebernehmen(result.id);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
  async artikelpositionUebernehmen(idBewe: number) {
    try {
      const paletten = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
        LlrPalleteStore.actions.loadMany,
        {
          clear: true,
          pathParams: {
            idkopfbskomm: this.kommissionierung?.id ?? '',
            idbewebskomm: idBewe ?? '' } }
      );

      if (paletten.length > 0 && this.remoteConfig.config.vorhandenePalettenAuswaehlbar) {
        await this.router.navigate([
          `/llr-bskomm/bewegung/${idBewe}/palettenart-auswahl`]);
        return;
      }

      const queryParams = this.remoteConfig.config.vorhandenePalettenAuswaehlbar
        ? { palettenVorhanden: false }
        : undefined;

      await this.router.navigate(
        [`/llr-bskomm/bewegung/${idBewe}/artikeldaten-erfassung`],
        {
          queryParams }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
